/**
 * @file Importer.js.
 * @author Sérgio Ferraz Souza <todentro@gmail.com>
 * @copyright Cognatis GeoAnalytics & Big Data Ltda :: 2021
 */

import Service from './Service'
import Request from '../components/http/request'
import { Postgrest } from './Postgrest'

const defaultJsonHeaders = {
  headers: {
    'Content-Type': 'application/json'
  }
}

class Importer extends Service {
  constructor (options, resource) {
    super(options)
    this.pgRest = Postgrest.build({})
    this.resource = resource
    this.request = new Request({tag: 'upload'})
    // this.request = new Request({environment: 'local', tag: 'upload'})
  }
  /**
   * Request a analyze file
   *
   * @async
   * @function AnalyzeFile
   * @param {guid} fileId - File UID
   * @return {Promise<json>} Array of the behaviors
   */
  async analyzeFile (fileId) {
    return new Promise((resolve, reject) => {
      const endPoint = `/api/0.1-a/importer/analyze/${fileId}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  /**
   * Start import process
   *
   * @async
   * @function beginImport
   * @param {guid} guid - Import UID
   * @return {Promise<json>} An identifier of the task that started the process.
  */
  async beginImport (guid) {
    return new Promise((resolve, reject) => {
      if (typeof guid === 'undefined') reject(new Error('Esperado o identificador de importação'))
      const endPoint = `/api/0.1-a/Importer/start/${guid}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  /**
   * Delete an upload file
   *
   * @async
   * @function delete
   * @param {Number} environment - Environment Id
   * @param {guid} guid - File UID
   * @return {Promise<json>} Array of the behaviors
   */
  async delete (environment, guid) {
    return new Promise((resolve, reject) => {
      const endPoint = `/api/0.1-a/importer/${environment}/${guid}`
      this.request.delete(endPoint, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  /**
   * Delete an source file
   *
   * @async
   * @function delete
   * @param {guid} fileUid - File UID
   * @return {Promise<json>} Array of the behaviors
   */
  async deleteFile ( fileUid) {
    return new Promise((resolve, reject) => {
      const endPoint = `/api/0.1-a/importer/file/${fileUid}`
      this.request.delete(endPoint, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
  /**
   * Get supported file behaviors
   *
   * @async
   * @function getBehaviors
   * @return {Promise<json>} Array of the behaviors
   */
  async getBehaviors () {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/Importer/SupportedBehaviors'
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get supported configuration
   * @function getConfiguration
   * @param {string} environment - Environment name
   * @returns {Promise<void>}
   */
  async getConfiguration (environment) {
    return new Promise((resolve, reject) => {
      this.pgRest.get('tbEnvironment', 'jbdata', `&cClientTag=eq.${environment}`)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get supported cultures
   *
   * @async
   * @function getSuperTypes
   * @return {Promise<json>} Array of the culture names
   */
  async getCultures () {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/Importer/SupportedCultures'
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get supported data types
   *
   * @async
   * @function getDataTypes
   * @return {Promise<json>} Array of the data types
   */
  async getDataTypes () {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/Importer/DefaultDataTypes'
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get supported character encode
   *
   * @async
   * @function getEncoding
   * @return {Promise<json>} Array of the encode
   */
  async getEncoding () {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/Importer/SupportedEncodings'
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get supported character encode
   *
   * @async
   * @function getEncoding
   * @param {Number} environment - Environment Id
   * @param {Number} skip -
   * @param {Number} take -
   * @return {Promise<json>} Array of the encode
   */
  async getFiles (environment, skip, take) {
    return new Promise((resolve, reject) => {
      const endPoint = `/api/0.1-a/importer/files/${environment}/${skip}/${take}`
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get upload stages
   * @async
   * @function getLastStage
   * @returns {Promise<void>}
   */
  async getLastStage () {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/importer/GetLastStage'
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Get supported super types
   *
   * @async
   * @function getSuperTypes
   * @return {Promise<json>} Array of the super types
   */
  async getSuperTypes () {
    return new Promise((resolve, reject) => {
      const endPoint = '/api/0.1-a/Importer/SupportedSuperTypes'
      this.request.get(endPoint, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Persist static data configuration
   *
   * @async
   * @function getStaticData
   * @param {guid} guid - File UID
   * @param {object} data - Data Options
   * @return {Promise<json>} Array of the static data
   */
  async getStaticData (guid, data) {
    return new Promise((resolve, reject) => {
      if (typeof guid === 'undefined') reject(new Error('Esperado o identificador de importação'))
      const endPoint = `/api/0.1-a/Importer/GetStaticData/${guid}`
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(response => resolve(response))
        .catch(() => reject)
    })
  }
  /**
   * Persist static data configuration and start import
   *
   * @async
   * @function postStaticData
   * @return {Promise<json>} Array of the static data
   */
  async postStaticData (guid, data = {}) {
    return new Promise((resolve, reject) => {
      if (typeof guid === 'undefined') reject(new Error('Esperado o identificador de importação'))
      const endPoint = `/api/0.1-a/Importer/PostStaticData/${guid}`
      this.request.post(endPoint, data, defaultJsonHeaders)
        .then(res => resolve(res))
        .catch(() => reject)
    })
  }
}

export { Importer }
